import { useState, useEffect } from "react";
import "./ParticipantModal.css";
import Tabs from "../DataTable/Tabs";
import {
  EditOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { fallbackDeep } from "../../utils/constants";
import { Spinner } from "../Filtering/Reusable/Spinner";
import CustomButton from "../buttons/CustomButton";
import { useTableControls } from "../../hooks/useTableControls";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { buildServiceUrl } from "../../utils/urlBuilder";
import axios from "axios";
import { toast } from "react-toastify";
import { useFilteredData } from "../../hooks/useFilteredData";
import DataTable from "../DataTable/DataTable";
import ConfirmDialog from "../DataTable/ConfirmDialog";
import { Popover } from "../DataTable/Popover";
import EditUserRolesForm from "../../pages/AdminDSB/AdminUtils/EditUserRolesForm";
import AddParticipantUserDialog from "../../pages/AdminDSB/AdminUtils/AddParticipantUserDialog";
import { useNavigate } from "react-router-dom";
import { getTextByLang } from "../../utils/formUtils";
import StatusChip from "../StatusChip/StatusChip";
import { useGetParticipantById } from "../../hooks/participants/useGetParticipantById";
import {
  getParticipantDisplayName,
  formatParticipantVocabularyValues,
  getParticipantVocabularyLabel,
} from "../../utils/participants";
import { useParticipantDisplayVocabularies } from "../../hooks/participants/useParticipantDisplayVocabularies";

const TABS = {
  IDENTIFICATION: "A",
  USERS: "B",
  CONNECTOR: "C",
};

export default function ParticipantModal({ onClose, participantId }) {
  const [activeTab, setActiveTab] = useState("A");
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [usersReloadKey, setUsersReloadKey] = useState(0);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { callApiWithWorker } = useWorkerApiHelper();
  const { isAdmin, token } = useAuthRoles();
  const [totalUsers, setTotalUsers] = useState(0);
  const handleSetTotalUsers = (count) => setTotalUsers(count);
  const { data: participant, isFetching } = useGetParticipantById({
    callApiWithWorker,
    participantId,
    lang: i18n.language,
  });
  const participantVocabularies = useParticipantDisplayVocabularies({
    callApiWithWorker,
    lang: i18n.language,
  });

  useEffect(() => {
    if (!token || !isAdmin) return;

    let cancelled = false;

    const fetchCount = async () => {
      try {
        const res = await axios.get(
          buildServiceUrl(
            "connectorMaster",
            `keycloak/internal/users?participant_id=${participantId}`,
          ),
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const count = Array.isArray(res.data)
          ? res.data.length
          : typeof res.data === "number"
            ? res.data
            : (res.data?.count ?? 0);

        if (!cancelled) setTotalUsers(count);
      } catch (e) {
        if (!cancelled) setTotalUsers(0);
        console.error("Error fetching user count:", e);
      }
    };

    fetchCount();

    return () => {
      cancelled = true;
    };
  }, [token, isAdmin, participantId]);

  const handleEdit = (data) => {
    navigate(`/editparticipant/${participantId}`, {
      state: { record: data },
    });
  };

  const tableConfigs = {
    [TABS.IDENTIFICATION]: {
      label: t("participant.idData"),
    },
    [TABS.USERS]: {
      label: (
        <>
          {t("usercontrol")} <span className="tab-badge">{totalUsers}</span>
        </>
      ),
    },
    // [TABS.CONNECTOR]: {
    //   label: t("connector"),
    // },
  };

  const renderActions = () => {
    switch (activeTab) {
      case TABS.IDENTIFICATION:
        return (
          <>
            <CustomButton
              variant="outlined"
              neutral
              startIcon={<EditOutlined />}
              onClick={() => handleEdit()}
            >
              {t("datatable.edit")}
            </CustomButton>
          </>
        );

      case TABS.USERS:
        return (
          <CustomButton
            variant="outlined"
            neutral
            onClick={() => setIsAddUserDialogOpen(true)}
            disabled={!isAdmin}
          >
            {t("datatable.addUser")}
          </CustomButton>
        );

      case TABS.CONNECTOR:
        return null;

      default:
        return null;
    }
  };

  if (isFetching || (participantId && participant === undefined))
    return (
      <div className="loading">
        <Spinner size="large" color="var(--main-color)" />
      </div>
    );
  if (!participant) return null;

  const participantDisplayName =
    getParticipantDisplayName(participant, i18n.language) ||
    participant?.legal_name ||
    participantId;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      onKeyDown={(event) => {
        if (
          event.key === "Escape" ||
          event.key === "Enter" ||
          event.key === " "
        ) {
          event.preventDefault();
          onClose();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Close participant modal"
    >
      <div
        className="modal participant-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="close">
          <button className="icon-btn" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="modal-actions">
          <h2>{t("participant.participantDetails")}</h2>
          <div className="header-actions">{renderActions()}</div>
        </div>

        <Tabs
          tabs={tableConfigs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          classNames={{
            container: "tabs-modal",
            button: "tabs-modal-btn",
            active: "tabs-modal-active",
          }}
        />
        <div className="modal-content">
          {activeTab === TABS.IDENTIFICATION && (
            <IdentificationTab
              data={participant}
              countryOptions={participantVocabularies.countries}
              purposeOptions={participantVocabularies.governmentFunctions}
            />
          )}
          {activeTab === TABS.USERS && (
            <UsersTab
              setTotalUsers={handleSetTotalUsers}
              participantId={participantId}
              reloadKey={usersReloadKey}
            />
          )}
          {/* {activeTab === TABS.CONNECTOR && <ConnectorTab data={participant} />} */}
        </div>
        <AddParticipantUserDialog
          open={isAddUserDialogOpen}
          onClose={() => setIsAddUserDialogOpen(false)}
          participantId={participantId}
          participantName={participantDisplayName}
          onSuccess={async () => {
            setUsersReloadKey((current) => current + 1);
          }}
        />
      </div>
    </div>
  );
}

function IdentificationTab({ data, countryOptions = [], purposeOptions = [] }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <div className="grid-modal">
        <Section title={t("participant.identity")}>
          <div className="row">
            <Field
              label={t("participant.name")}
              value={fallbackDeep(
                getTextByLang(data?.preferredLabel) ||
                  getTextByLang(data?.identifier?.[0]?.schemeName),
              )}
            />
          </div>
          <div className="row">
            <Field
              label={t("participant.nif")}
              value={fallbackDeep(data?.identifier?.[0]?.notation)}
            />
            <Field
              label={t("participant.purpose")}
              value={fallbackDeep(
                formatParticipantVocabularyValues(
                  data?.purpose,
                  purposeOptions,
                  i18n.language,
                ),
              )}
            />
          </div>
        </Section>

        <Section title={t("participant.localization")}>
          <div className="row">
            <Field
              label={t("participant.country")}
              value={fallbackDeep(
                getParticipantVocabularyLabel(
                  data?.address?.adminUnit1,
                  countryOptions,
                  i18n.language,
                ),
              )}
            />
            <Field
              label={t("participant.address")}
              value={fallbackDeep(data?.address?.fullAddress)}
            />
          </div>
          <div className="row">
            <Field
              label={t("participant.city")}
              value={fallbackDeep(data?.address?.postName)}
            />
            <Field
              label={t("participant.postal")}
              value={fallbackDeep(
                data?.address?.postCode ?? data?.address?.postalCode,
              )}
            />
          </div>
        </Section>

        <Section title={t("participant.contactInformation")}>
          <div className="row">
            <Field
              label={t("participant.email")}
              value={fallbackDeep(data?.contactPoint?.email)}
              rawValue={data?.contactPoint?.email}
              link
            />
          </div>
          <div className="row">
            <Field
              label={t("participant.phone")}
              value={fallbackDeep(data?.contactPoint?.telephone)}
            />
          </div>
        </Section>

        {/* <Section title={t("participant.metadata")}>
          <div className="row">
            <Field
              label={t("participant.registered")}
              value={fallbackDeep(
                new Date(
                  data?.updated_at ??
                    data?.created_at ??
                    data?.registration_date,
                ).toLocaleDateString("es-ES", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }),
              )}
            />
            <Field
              label={t("participant.status")}
              value={
                <span className={`status ${connectorStatus?.toLowerCase()}`}>
                  {fallbackDeep(translatedStatus)}
                </span>
              }
            />
          </div>
        </Section> */}
      </div>

      <Section title={t("participant.description")} full>
        <div className="row">
          {fallbackDeep(getTextByLang(data?.description))}
        </div>
      </Section>
    </>
  );
}

function UsersTab({ setTotalUsers, participantId, reloadKey }) {
  const {
    tableData,
    setTableData,
    searchTerm,
    filters,
    pagination,
    setPagination,
    rowCount,
    setRowCount,
  } = useTableControls([]);
  const { t } = useTranslation();
  const { dialogOpen, dialogConfig, openDialog, closeDialog } =
    useConfirmDialog();
  const { isAdmin, token, decoded } = useAuthRoles();
  const [openPetition, setOpenPetition] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleOpenPetition = (row) => {
    setSelectedUser(row);
    setOpenPetition(true);
  };

  const handleToggleUser = (user) => {
    const enable = !user.enabled;

    openDialog({
      title: enable
        ? t("confirmDialog.enableTitle")
        : t("confirmDialog.disableTitle"),
      message: enable
        ? t("confirmDialog.enableMessage", { username: user.username })
        : t("confirmDialog.disableMessage", { username: user.username }),
      onConfirm: async () => {
        await toggleUserEnabled(user.id, enable);
        const updated = await listPendingUsers();
        setTableData(updated);
      },
    });
  };

  const handleDeleteUser = (user) => {
    openDialog({
      title: t("confirmDialog.deleteTitle"),
      message: t("confirmDialog.deleteMessage", { username: user.username }),
      onConfirm: async () => {
        await deleteUser(user.id);
        const updated = await listPendingUsers();
        setTableData(updated);
      },
    });
  };

  const handleCloseSession = (user) => {
    openDialog({
      title: t("confirmDialog.sessionTitle"),
      message: t("confirmDialog.sessionMessage", { username: user.username }),
      onConfirm: async () => {
        await logoutUser(user.id);
        const updated = await listPendingUsers();
        setTableData(updated);
      },
    });
  };

  async function toggleUserEnabled(userId, enable) {
    const userToken = token;
    const res = await axios.get(
      `${buildServiceUrl("keycloak", `admin/realms/DSB/users/${userId}`)}`,
      { headers: { Authorization: `Bearer ${userToken}` } },
    );
    const user = res.data;
    try {
      await axios.put(
        `${buildServiceUrl("keycloak", `admin/realms/DSB/users/${userId}`)}`,
        { ...user, enabled: enable },
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      toast.success(t("toaster.userStatus"));
    } catch (error) {
      toast.error(t("toaster.userError"));
    }
  }

  async function logoutUser(userId) {
    const userToken = token;
    try {
      await axios.post(
        `${buildServiceUrl(
          "keycloak",
          `admin/realms/DSB/users/${userId}/logout`,
        )}`,
        {},
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      toast.success(t("toaster.userSession"));
    } catch (error) {
      toast.error(t("toaster.userError"));
    }
  }

  async function getTotalUserCount(userToken) {
    try {
      const res = await axios.get(
        buildServiceUrl(
          "connectorMaster",
          `keycloak/internal/users?participant_id=${participantId}`,
        ),
        { headers: { Authorization: `Bearer ${userToken}` } },
      );

      return Array.isArray(res.data)
        ? res.data.filter((u) => u.id !== decoded.sub).length
        : 0;
    } catch (error) {
      console.error("Error fetching user count:", error);
      return 0;
    }
  }

  async function deleteUser(userId) {
    const userToken = token;
    try {
      await axios.delete(
        `${buildServiceUrl("keycloak", `admin/realms/DSB/users/${userId}`)}`,
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      toast.success(t("toaster.userDeleted"));
    } catch (error) {
      toast.error(t("toaster.userError"));
    }
  }

  const columns = [
    {
      header: t("usermanagement.name"),
      accessorKey: "firstName",
      meta: { width: "10%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <span className="bold">{getValue()}</span>
        </div>
      ),
    },
    {
      header: t("register.surname"),
      accessorKey: "lastName",
      meta: { width: "20%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <span className="bold">{getValue()}</span>
        </div>
      ),
    },
    {
      header: t("usermanagement.email"),
      accessorKey: "email",
      meta: { width: "20%" },
    },
    {
      header: t("usermanagement.role"),
      accessorKey: "role",
      meta: { width: "20%" },
      cell: ({ getValue }) => {
        const roles = getValue();
        if (!roles || (Array.isArray(roles) && roles.length === 0)) {
          return null;
        }
        return (
          <div className="centered-cell highlight-container">
            {Array.isArray(roles) ? (
              roles.map((r, idx) => (
                <span key={idx} className={`roles_span role-${r}`}>
                  {t(`roles.${r}`, r)}
                </span>
              ))
            ) : (
              <span className={`roles_span role-${roles}`}>
                {t(`roles.${roles}`, roles)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: t("profilePage.statusTitle"),
      accessorKey: "enabled",
      meta: { width: "15%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <StatusChip
            label={t(`profilePage.status.${getValue()}`)}
            value={String(getValue())}
          />
        </div>
      ),
    },
    {
      header: t("profilePage.personalData.organization"),
      accessorFn: (row) => row.attributes?.organization ?? "",
      meta: { width: "20%" },
    },
  ];
  const filteredData = useFilteredData(tableData, searchTerm, filters, columns);

  async function listPendingUsers() {
    const userToken = token;
    const first = (pagination.pageIndex - 1) * pagination.pageSize;
    const max = pagination.pageSize;

    const res = await axios.get(
      `${buildServiceUrl(
        "connectorMaster",
        `keycloak/internal/users?participant_id=${participantId}&page=${first}&size=${max}`,
      )}`,
      { headers: { Authorization: `Bearer ${userToken}` } },
    );

    const users = Array.isArray(res.data) ? res.data : [];
    const otherUsers = users.filter((u) => u.id !== decoded.sub);

    const usersWithExtraData = await Promise.all(
      otherUsers.map(async (userItem) => {
        try {
          const rolesRes = await axios.get(
            `${buildServiceUrl(
              "keycloak",
              `admin/realms/DSB/users/${userItem.id}/role-mappings/realm`,
            )}`,
            { headers: { Authorization: `Bearer ${userToken}` } },
          );
          const roles = rolesRes.data
            .map((r) => r.name)
            .filter((r) => r !== "default-roles-dsb");

          const sessionsRes = await axios.get(
            `${buildServiceUrl(
              "keycloak",
              `admin/realms/DSB/users/${userItem.id}/sessions`,
            )}`,
            { headers: { Authorization: `Bearer ${userToken}` } },
          );
          const hasActiveSession = sessionsRes.data.length > 0;

          return {
            ...userItem,
            role: roles,
            hasActiveSession,
          };
        } catch (err) {
          console.error(
            `Error fetching data for user ${userItem.username}:`,
            err,
          );
          return { ...userItem, role: "N/A", hasActiveSession: false };
        }
      }),
    );

    return usersWithExtraData;
  }

  useEffect(() => {
    if (!token || !isAdmin) return;

    const fetchUsers = async () => {
      const totalCount = await getTotalUserCount(token);

      setRowCount(totalCount);
      setTotalUsers?.(totalCount);

      const users = await listPendingUsers();
      setTableData(users);
    };

    fetchUsers().catch((err) => console.error("Error fetching users:", err));
  }, [
    token,
    isAdmin,
    participantId,
    pagination.pageIndex,
    pagination.pageSize,
    reloadKey,
    setTotalUsers,
  ]);

  return (
    <div>
      <DataTable
        columns={columns}
        data={filteredData}
        showActions={isAdmin}
        onPermission={handleOpenPetition}
        onInhability={handleToggleUser}
        onDeleteUser={handleDeleteUser}
        onCloseSession={handleCloseSession}
        pagination={pagination}
        setPagination={setPagination}
        rowCount={rowCount}
      />
      <ConfirmDialog
        open={dialogOpen}
        title={dialogConfig.title}
        message={dialogConfig.message}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
        onClose={closeDialog}
        onConfirm={async () => {
          if (dialogConfig.onConfirm) await dialogConfig.onConfirm();
          closeDialog();
        }}
      />
      {openPetition && selectedUser ? (
        <Popover open={openPetition} onClose={() => setOpenPetition(false)}>
          <EditUserRolesForm
            userInfo={selectedUser}
            closePopover={() => setOpenPetition(false)}
            onUpdateTable={listPendingUsers}
            setTableData={setTableData}
          />
        </Popover>
      ) : null}
    </div>
  );
}

export function Section({ title, children, full }) {
  return (
    <div
      className={`section participant-section ${full ? "full participant-section-full" : ""}`}
    >
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Field({ label, value, rawValue, link }) {
  const hasValue =
    rawValue !== null && rawValue !== undefined && rawValue !== "";

  return (
    <div className="field participant-field">
      <span className="label participant-label">{label}</span>

      {link && hasValue ? (
        <a href={`mailto:${rawValue}`} className="value participant-value link">
          {value}
        </a>
      ) : (
        <span className="value participant-value">{value}</span>
      )}
    </div>
  );
}
